import { useEffect, useCallback, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import SearchIcon from "~/components/SearchIcon"
import { JobIntegration } from "~/types/integrations";
import TextInput from "~/shared/TextInput";
import Spinner from "~/shared/Spinner";
import { LocatedJob } from "~/types/jobs";
import DataTable from "~/shared/DataTable";
import CheckboxInput from "~/shared/CheckboxInput";
import Button from "~/shared/Btn";
import api from "~/adapters/api";

type VistaCRMConfigProps = {
    StepEl: any;
    integration: JobIntegration;
    job: LocatedJob;
    files: any[]
};

const FormContainer = styled.form`
    position: relative;

    button {
        position: absolute;
        top: 1px;
        right: 1px;
        background: transparent;
        border-radius: 100%;
        height: 38px;
        overflow: hidden;
    }
`;

const VistaCRMConfig = ({ StepEl, job, files, integration }: VistaCRMConfigProps) => {
    const [code, setCode] = useState("");
    const [searchingCode, setSearchingCode] = useState(false);
    const [searchCode, setSearchCode] = useState(job?.customerCode ?? "");
    const [selecteds, setSelecteds] = useState(files.map(file => file.id));
    const [featured, setFeatured] = useState(null);
    const [executing, setExecuting] = useState(false);

    const fetchRealEstateData = useCallback((integrationId: number, customerCode: string) => {
        setSearchingCode(true);
        api.jobs.integration.searchEntity({ integrationId, reference: customerCode })
            .then(response => {
                if (response.status === "error") {
                    toast.error(response.message);
                    return;
                }

                setCode(response?.data?.Codigo);
            })
            .catch(async e => {
                console.log(e.response);
                toast.error(`Não foi possível acessar o VistaCRM: ${e?.response?.data?.msg || e.message}`);
            })
            .finally(() => setSearchingCode(false));
    }, []);

    useEffect(() => {
        if (!job.customerCode) {
            return;
        }

        fetchRealEstateData(integration.id, job.customerCode);
    }, [job?.customerCode, integration?.id]);

    useEffect(() => {
        if (!selecteds.includes(featured)) {
            setFeatured(null);
        }
    }, [selecteds, featured]);

    const searchIntegration = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setCode("");

        fetchRealEstateData(integration.id, searchCode);
    };

    const toggleSelecteds = (id: number) => {
        setSelecteds(sels => {
            if (sels.includes(id)) {
                return sels.filter(item => item !== id);
            }

            return sels.concat([id]);
        });
    };

    const executeIntegration = () => {
        setExecuting(true);

        const integrationFiles = selecteds.length
            ? selecteds.map(fileId => ({ fileId, featured: fileId === featured }))
            : files.map(file => ({ fileId: file.id, featured: file.id === featured }));

        const request = {
            files: integrationFiles,
            jobId: job.id,
            integrationId: integration.id,
            reference: searchCode
        };

        api.jobs.integration.executeIntegration(request)
            .then(response => {
                if (response.executionStatus === "erro") {
                    toast.error("VistaCRM: Ocorreu um erro durante o processo de envio de fotos.");
                    return;
                }

                toast.success("VistaCRM: Fotos importadas com sucesso.");
            })
            .catch(async e => {
                toast.error(`VistaCRM: ${e?.response?.data?.msg || e.message}`);
            })
            .finally(() => setExecuting(false));
    };

    return (
        <>
            <StepEl>
                <div className="step-number"><span>2</span></div>
                <div>
                    <h4>Em qual imóvel?</h4>
                    <p><small>Informe o código do imóvel no VistaCRM</small></p>
                    <FormContainer onSubmit={searchIntegration} className="mt-2">
                        <TextInput
                            name="code"
                            onChange={(_, value) => setSearchCode(value)}
                            value={searchCode}
                        />
                        <button type="submit">
                            <SearchIcon />
                        </button>
                        {searchingCode ? <Spinner /> : ""}
                    </FormContainer>
                </div>
            </StepEl>
            {code ? (
                <StepEl>
                    <div className="step-number"><span>3</span></div>
                    <div style={{ width: "100%" }}>
                        <h4 className="mb-2">Fotos</h4>

                        <div style={{ maxHeight: "250px", overflow: "auto" }}>
                            <DataTable records={files} loading={false}>
                                <DataTable.Computed
                                    style={{ width: "25px" }}
                                    render={(row: any) => (
                                        <CheckboxInput name="check" onChange={() => toggleSelecteds(row.id)} value={selecteds.includes(row.id)} />
                                    )}
                                />
                                <DataTable.Computed
                                    style={{ width: "125px" }}
                                    render={(row: any) => (
                                        <img src={row.thumb} style={{ maxWidth: "100%" }} />
                                    )}
                                />
                                <DataTable.Computed
                                    style={{ width: "75px", textAlign: "center" }}
                                    label="Destaque?"
                                    render={(row: any) => (
                                        <Button
                                            small
                                            variant={row.id === featured ? "primary" : "secondary"}
                                            onClick={() => setFeatured(row.id)}
                                        >
                                            {row.id === featured ? "Sim" : "Não"}
                                        </Button>
                                    )}
                                />
                                <DataTable.Text field="originalFilename" label="Arquivo" />
                            </DataTable>
                        </div>

                        <div className="mt-3" style={{ float: "right" }}>
                            <Button loading={executing} onClick={executeIntegration}>Executar Integração</Button>
                        </div>
                    </div>
                </StepEl>
            ) : (
                ""
            )}
        </>
    );
};

export default VistaCRMConfig;
